import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Warning } from "@phosphor-icons/react";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEBOUNCE_MS = 500;

// Warns, while someone is still typing, that a link is already on the site.
//
// This is a convenience, not a gate: the server rejects duplicates on submit
// regardless (409), so a missed check here costs a round trip, never a bad row.
// Checking on every keystroke would hammer the endpoint mid-paste, so the value
// is debounced and stale responses are dropped by request id — a slow reply for
// an old URL must never overwrite the answer for the current one.
const DuplicateLinkNotice = ({ url, includePending = true, onResult }) => {
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const reqRef = useRef(0);
  const reportedRef = useRef(null);

  useEffect(() => {
    const value = (url || "").trim();
    const mine = ++reqRef.current;

    const settle = (result) => {
      if (reqRef.current !== mine) return; // a newer check has started
      setExisting(result);
      onResult?.(result);
    };

    if (!value || !/^https?:\/\/\S+$/i.test(value)) {
      settle(null);
      return;
    }

    const t = setTimeout(() => {
      axios
        .get(`${API}/products/check-link`, {
          params: { url: value, include_pending: includePending },
        })
        .then((res) => {
          const dupe = res.data?.duplicate ? res.data.existing : null;
          // Only report a given duplicate once per link, so re-renders don't
          // inflate the count.
          if (dupe && reportedRef.current !== value) {
            reportedRef.current = value;
            track("duplicate_link_detected", {
              where: dupe.where,
              existing_product_id: dupe.product_id,
              stage: "typing",
            });
          }
          settle(dupe);
        })
        .catch(() => settle(null)); // never block on a failed check
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, includePending]);

  if (!existing) return null;

  const isLive = existing.where === "live";
  return (
    <div
      className="mt-2 flex items-start gap-2 rounded-md border border-[#E9C46A] bg-[#FDF6E3] px-3 py-2"
      data-testid="duplicate-link-notice"
      role="status"
    >
      <Warning size={16} weight="fill" className="mt-[2px] shrink-0 text-[#B7791F]" />
      <p className="text-xs text-[#6B5A2A] leading-relaxed">
        {isLive ? (
          <>
            Already on the site
            {existing.product_name ? ` as "${existing.product_name}"` : ""}.
            {existing.product_id && (
              <>
                {" "}
                <button
                  type="button"
                  onClick={() => {
                    track("duplicate_link_view_existing", {
                      existing_product_id: existing.product_id,
                    });
                    navigate(`/product/${existing.product_id}`);
                  }}
                  className="underline hover:no-underline font-medium"
                  data-testid="duplicate-link-view"
                >
                  View it
                </button>
              </>
            )}
          </>
        ) : (
          <>Someone already shared this link — it's waiting to be reviewed.</>
        )}
      </p>
    </div>
  );
};

export default DuplicateLinkNotice;
